import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import ReportBuilderModal from '../components/modals/ReportBuilderModal';
import { usePlan } from '../context/PlanContext';
import { useOrgSwitchRedirect } from '../hooks/useOrgSwitchRedirect';
import { fetchWithOrg } from '../utils/api';
import { useNavigate } from 'react-router-dom';

import {
  DownloadSimple,
  FileText,
  FloppyDisk,
  Layout,
  Lock,
  Trash,
  UploadSimple
} from '@phosphor-icons/react';
import { WorkspaceHeader, WorkspaceContent } from '../components/WorkspaceLayout';
const ReportsPage = () => {
  const navigate = useNavigate();
  const { isReady, activeOrganization } = useOrgSwitchRedirect();
  const { userPlan, planLimits } = usePlan();
  const [activeTab, setActiveTab] = useState('generate');
  const [showReportBuilder, setShowReportBuilder] = useState(false);
  
  // Branding state
  const [branding, setBranding] = useState({
    logo_url: '',
    business_name: '',
    consultant_name: '',
    email: '',
    phone: '',
    website: '',
    address: '',
    disclaimer_override: '',
    use_own_branding: false
  });
  
  const [orgMetadata, setOrgMetadata] = useState({
    custom_notes: '',
    additional_fields: {}
  });
  
  const [isPartnerPro, setIsPartnerPro] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Report History state
  const [reportHistory, setReportHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (isReady) {
      loadBranding();
      if (activeTab === 'history') {
        loadReportHistory();
      }
    }
  }, [isReady, activeTab]);

  const loadBranding = async () => {
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/report/branding`,
        {},
        activeOrganization?.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        setIsPartnerPro(data.is_partner_pro);
        setBranding(data.partner_branding || {});
        setOrgMetadata(data.org_metadata || {});
      }
    } catch (error) {
      console.error('Failed to load branding:', error);
    }
  };

  const handleSaveBranding = async () => {
    setSaving(true);
    try {
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/report/branding`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...branding,
            custom_notes: orgMetadata.custom_notes,
            additional_fields: orgMetadata.additional_fields
          })
        },
        activeOrganization?.organization_id
      );

      if (response.ok) {
        alert('Branding settings saved successfully!');
      }
    } catch (error) {
      console.error('Error saving branding:', error);
      alert('Failed to save branding settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('logo', file);

    try {
      const response = await fetch(`${BACKEND_URL}/api/report/branding/upload-logo`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setBranding(prev => ({ ...prev, logo_url: data.logo_url }));
        alert('Logo uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Failed to upload logo');
    }
  };

  // Report History functions
  const loadReportHistory = async () => {
    if (!activeOrganization?.organization_id) return;
    
    try {
      setLoadingHistory(true);
      const response = await fetch(
        `${BACKEND_URL}/api/reports/history?organization_id=${activeOrganization.organization_id}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setReportHistory(data.reports || []);
      }
    } catch (error) {
      console.error('Error loading report history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleDownloadReport = async (reportId, filename) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/reports/history/${reportId}`,
        {
          method: 'GET',
          credentials: 'include'
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error downloading report:', error);
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!confirm('Are you sure you want to delete this report from history?')) return;

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/reports/history/${reportId}`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (response.ok) {
        loadReportHistory();
      }
    } catch (error) {
      console.error('Error deleting report:', error);
    }
  };

  return (
      <>
      <div className="min-h-screen bg-[var(--surface-0)]">
      <WorkspaceHeader
        icon={FileText}
        title="Reports"
        description="Manage branding, create presets, and generate professional AI opportunity reports"
      >
        {/* Tabs */}
        <div className="flex gap-1">
          {[
            { id: 'generate', label: 'Generate Report', icon: DownloadSimple },
            { id: 'branding', label: 'Branding & Details', icon: Layout },
            { id: 'history', label: 'Report History', icon: FileText }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-3.5 py-2 text-[13px] font-medium rounded-md transition-colors ${
                activeTab === tab.id
                  ? 'bg-[var(--surface-3)] text-white'
                  : 'text-[var(--text-muted)] hover:text-white hover:bg-[var(--surface-2)]'
              }`}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>
      </WorkspaceHeader>

      <WorkspaceContent>
          {/* Generate Report Tab */}
          {activeTab === 'generate' && (
            <div className="max-w-4xl">
              {/* Plan Gate: Reports available on Pro and Partner Pro only */}
              {!planLimits.has_reports ? (
                <div className="glass rounded-2xl p-12 text-center border-2 border-[rgba(99,102,241,0.3)]">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[rgba(99,102,241,0.1)] border-2 border-[rgba(99,102,241,0.3)] mb-6">
                    <Lock size={40} className="text-[#6366F1]" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-3">
                    Reports & PDF Exports
                  </h2>
                  <p className="text-[#A0AEC0] mb-2 max-w-2xl mx-auto">
                    Professional PDF reports with custom branding are available on Pro and Partner Pro plans.
                  </p>
                  <p className="text-sm text-[#718096] mb-8 max-w-xl mx-auto">
                    Generate comprehensive AI opportunity reports, customize branding, and export professional PDFs for your clients.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <button
                      onClick={() => navigate('/billing')}
                      className="px-8 py-4 rounded-xl bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white font-semibold hover:opacity-90 transition-opacity flex items-center gap-3 justify-center"
                    >
                      Upgrade to Pro
                    </button>
                    <button
                      onClick={() => navigate('/help')}
                      className="px-8 py-4 rounded-xl glass text-white font-semibold hover:bg-[rgba(255,255,255,0.1)] transition-colors flex items-center gap-3 justify-center"
                    >
                      Learn More
                    </button>
                  </div>
                </div>
              ) : (
                <div className="glass rounded-2xl p-8 text-center">
                  <FileText size={64} className="mx-auto mb-4 text-[#6366F1]" />
                  <h2 className="text-2xl font-bold text-white mb-3">
                    Generate AI Opportunity Report
                  </h2>
                  <p className="text-[#A0AEC0] mb-6 max-w-2xl mx-auto">
                    Create a professional, customized report with your selected sections and branding.
                    Choose from presets or customize every detail.
                  </p>
                  <button
                    onClick={() => setShowReportBuilder(true)}
                    className="px-8 py-4 rounded-xl bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white font-semibold hover:opacity-90 transition-opacity flex items-center gap-3 mx-auto"
                  >
                    <DownloadSimple size={20} />
                    Generate Report
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Branding Tab */}
          {activeTab === 'branding' && (
            <div className="max-w-4xl">
              {!isPartnerPro && (
                <div className="mb-6 p-4 rounded-xl bg-[rgba(99,102,241,0.1)] border border-[rgba(99,102,241,0.3)]">
                  <p className="text-sm text-[#6366F1]">
                    <strong>Partner Pro</strong> subscription required for custom branding.
                    Upgrade to add your logo and business details.
                  </p>
                </div>
              )}

              <div className="space-y-6">
                {/* Partner Pro Branding */}
                <div className="glass rounded-xl p-6">
                  <h3 className="text-xl font-bold text-white mb-4">Partner Pro Branding</h3>
                  <p className="text-sm text-[#A0AEC0] mb-6">
                    These settings persist across all your organizations
                  </p>

                  <div className="space-y-4">
                    {/* Logo UploadSimple */}
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Logo
                      </label>
                      <div className="flex items-center gap-4">
                        {branding.logo_url && (
                          <img
                            src={branding.logo_url}
                            alt="Logo"
                            className="w-32 h-16 object-contain bg-white rounded-lg p-2"
                          />
                        )}
                        <label className="px-4 py-2 rounded-lg glass hover:bg-[rgba(255,255,255,0.1)] text-white cursor-pointer flex items-center gap-2">
                          <UploadSimple size={16} />
                          UploadSimple Logo
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                            disabled={!isPartnerPro}
                          />
                        </label>
                      </div>
                    </div>

                    {/* Branding Mode Toggle */}
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={branding.use_own_branding}
                        onChange={(e) => setBranding(prev => ({ ...prev, use_own_branding: e.target.checked }))}
                        disabled={!isPartnerPro}
                        className="w-4 h-4 rounded border-[rgba(255,255,255,0.2)] bg-[rgba(255,255,255,0.05)] checked:bg-[#6366F1]"
                      />
                      <label className="text-white">Use My Branding (Hide &quot;Powered by Man &amp; Machine&quot;)</label>
                    </div>

                    {/* Business Details */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-white mb-2">
                          Business Name
                        </label>
                        <input
                          type="text"
                          value={branding.business_name || ''}
                          onChange={(e) => setBranding(prev => ({ ...prev, business_name: e.target.value }))}
                          disabled={!isPartnerPro}
                          className="w-full px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-[#718096] focus:outline-none focus:ring-2 focus:ring-[#6366F1] disabled:opacity-50"
                          placeholder="Your Business Name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white mb-2">
                          Consultant Name
                        </label>
                        <input
                          type="text"
                          value={branding.consultant_name || ''}
                          onChange={(e) => setBranding(prev => ({ ...prev, consultant_name: e.target.value }))}
                          disabled={!isPartnerPro}
                          className="w-full px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-[#718096] focus:outline-none focus:ring-2 focus:ring-[#6366F1] disabled:opacity-50"
                          placeholder="Your Name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          value={branding.email || ''}
                          onChange={(e) => setBranding(prev => ({ ...prev, email: e.target.value }))}
                          disabled={!isPartnerPro}
                          className="w-full px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-[#718096] focus:outline-none focus:ring-2 focus:ring-[#6366F1] disabled:opacity-50"
                          placeholder="contact@example.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white mb-2">
                          Phone
                        </label>
                        <input
                          type="tel"
                          value={branding.phone || ''}
                          onChange={(e) => setBranding(prev => ({ ...prev, phone: e.target.value }))}
                          disabled={!isPartnerPro}
                          className="w-full px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-[#718096] focus:outline-none focus:ring-2 focus:ring-[#6366F1] disabled:opacity-50"
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white mb-2">
                          Website
                        </label>
                        <input
                          type="url"
                          value={branding.website || ''}
                          onChange={(e) => setBranding(prev => ({ ...prev, website: e.target.value }))}
                          disabled={!isPartnerPro}
                          className="w-full px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-[#718096] focus:outline-none focus:ring-2 focus:ring-[#6366F1] disabled:opacity-50"
                          placeholder="https://example.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white mb-2">
                          Address
                        </label>
                        <input
                          type="text"
                          value={branding.address || ''}
                          onChange={(e) => setBranding(prev => ({ ...prev, address: e.target.value }))}
                          disabled={!isPartnerPro}
                          className="w-full px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-[#718096] focus:outline-none focus:ring-2 focus:ring-[#6366F1] disabled:opacity-50"
                          placeholder="123 Main St, City, State"
                        />
                      </div>
                    </div>

                    {/* Disclaimer Override */}
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Custom Disclaimer (Optional)
                      </label>
                      <textarea
                        value={branding.disclaimer_override || ''}
                        onChange={(e) => setBranding(prev => ({ ...prev, disclaimer_override: e.target.value }))}
                        disabled={!isPartnerPro}
                        className="w-full px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-[#718096] focus:outline-none focus:ring-2 focus:ring-[#6366F1] min-h-[100px] disabled:opacity-50"
                        placeholder="Enter custom disclaimer text..."
                      />
                    </div>
                  </div>
                </div>

                {/* Organization-Specific Fields */}
                <div className="glass rounded-xl p-6">
                  <h3 className="text-xl font-bold text-white mb-4">
                    Organization-Specific Settings
                  </h3>
                  <p className="text-sm text-[#A0AEC0] mb-6">
                    These settings are specific to {activeOrganization?.name || 'this organization'}
                  </p>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Custom Notes for Reports
                      </label>
                      <textarea
                        value={orgMetadata.custom_notes || ''}
                        onChange={(e) => setOrgMetadata(prev => ({ ...prev, custom_notes: e.target.value }))}
                        className="w-full px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white placeholder-[#718096] focus:outline-none focus:ring-2 focus:ring-[#6366F1] min-h-[100px]"
                        placeholder="Add organization-specific notes that will appear in reports..."
                      />
                    </div>
                  </div>
                </div>

                {/* FloppyDisk Button */}
                <div className="flex justify-end">
                  <button
                    onClick={handleSaveBranding}
                    disabled={saving || !isPartnerPro}
                    className="px-6 py-3 rounded-xl bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
                  >
                    {saving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FloppyDisk size={18} />
                        Save Branding Settings
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Report History Tab */}
          {activeTab === 'history' && (
            <div className="max-w-6xl">
              {/* Plan Gate: Report History only for Pro+ */}
              {!planLimits.has_reports ? (
                <div className="glass rounded-2xl p-12 text-center border-2 border-[rgba(99,102,241,0.3)]">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[rgba(99,102,241,0.1)] border-2 border-[rgba(99,102,241,0.3)] mb-6">
                    <Lock size={40} className="text-[#6366F1]" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-3">
                    Report History
                  </h2>
                  <p className="text-[#A0AEC0] mb-8 max-w-2xl mx-auto">
                    Report history is available on Pro and Partner Pro plans. Upgrade to access your generated reports.
                  </p>
                  <button
                    onClick={() => navigate('/billing')}
                    className="px-8 py-4 rounded-xl bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white font-semibold hover:opacity-90 transition-opacity"
                  >
                    Upgrade to Pro
                  </button>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-white">Report History</h3>
                    <p className="text-sm text-[#A0AEC0] mt-1">
                      View and download previously generated reports
                    </p>
                  </div>

                  {loadingHistory ? (
                    <div className="glass rounded-xl p-12 text-center">
                      <div className="w-12 h-12 border-4 border-[#6366F1] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                      <p className="text-[#A0AEC0]">Loading report history...</p>
                    </div>
                  ) : reportHistory.length === 0 ? (
                    <div className="glass rounded-xl p-12 text-center">
                      <FileText size={64} className="mx-auto mb-4 text-[#A0AEC0] opacity-50" />
                      <h3 className="text-lg font-semibold text-white mb-2">No Reports Yet</h3>
                      <p className="text-[#A0AEC0] mb-6">
                        Generate your first report to see it here
                      </p>
                      <button
                        onClick={() => setActiveTab('generate')}
                        className="px-6 py-3 rounded-xl bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white font-semibold hover:opacity-90 transition-opacity"
                      >
                        Generate Report
                      </button>
                    </div>
                  ) : (
                <div className="space-y-4">
                  {reportHistory.map((report) => {
                    const generatedDate = new Date(report.generated_at);
                    const formattedDate = generatedDate.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    });
                    const formattedTime = generatedDate.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit'
                    });

                    return (
                      <div
                        key={report.id}
                        className="glass rounded-xl p-6 hover:bg-[rgba(255,255,255,0.05)] transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <FileText size={20} className="text-[#6366F1]" />
                              <h4 className="text-lg font-semibold text-white">
                                {report.filename}
                              </h4>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
                              <div>
                                <p className="text-[#A0AEC0] text-xs mb-1">Generated</p>
                                <p className="text-white font-medium">{formattedDate}</p>
                                <p className="text-[#A0AEC0] text-xs">{formattedTime}</p>
                              </div>
                              
                              <div>
                                <p className="text-[#A0AEC0] text-xs mb-1">Opportunities</p>
                                <p className="text-white font-medium">{report.opportunities_count || 0}</p>
                              </div>
                              
                              <div>
                                <p className="text-[#A0AEC0] text-xs mb-1">File Size</p>
                                <p className="text-white font-medium">
                                  {report.file_size_kb ? `${report.file_size_kb.toFixed(1)} KB` : 'N/A'}
                                </p>
                              </div>
                              
                              <div>
                                <p className="text-[#A0AEC0] text-xs mb-1">Generated By</p>
                                <p className="text-white font-medium text-xs truncate">
                                  {report.user_email || 'Unknown'}
                                </p>
                              </div>
                            </div>

                            {/* Report Config Summary */}
                            {report.report_config && (
                              <div className="flex flex-wrap gap-2 mt-4">
                                {report.report_config.include_executive_summary && (
                                  <span className="px-2 py-1 rounded bg-[rgba(16,185,129,0.1)] text-[#10B981] text-xs">
                                    Executive Summary
                                  </span>
                                )}
                                {report.report_config.include_roi_table && (
                                  <span className="px-2 py-1 rounded bg-[rgba(16,185,129,0.1)] text-[#10B981] text-xs">
                                    ROI Table
                                  </span>
                                )}
                                {report.report_config.include_roadmap && (
                                  <span className="px-2 py-1 rounded bg-[rgba(16,185,129,0.1)] text-[#10B981] text-xs">
                                    Roadmap
                                  </span>
                                )}
                                {report.report_config.include_ai_agent_blueprints && (
                                  <span className="px-2 py-1 rounded bg-[rgba(16,185,129,0.1)] text-[#10B981] text-xs">
                                    Agent Blueprints
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => handleDownloadReport(report.id, report.filename)}
                              className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white hover:opacity-90 transition-opacity flex items-center gap-2"
                              title="Download Report"
                            >
                              <DownloadSimple size={16} />
                              DownloadSimple
                            </button>
                            <button
                              onClick={() => handleDeleteReport(report.id)}
                              className="p-2 hover:bg-[rgba(239,68,68,0.1)] rounded-lg transition-colors"
                              title="Delete Report"
                            >
                              <Trash size={16} className="text-[#EF4444]" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
                </>
              )}
            </div>
          )}
      </WorkspaceContent>
      </div>

      {/* Report Builder Modal */}
      <ReportBuilderModal
        isOpen={showReportBuilder}
        onClose={() => setShowReportBuilder(false)}
        organizationId={activeOrganization?.organization_id}
        onGenerate={() => {
          console.log('Report generated successfully');
        }}
      />
    </>
  );
};

export default ReportsPage;
